/**
 * mercadoPagoMapper.ts — PURE module (no React, no fetch, no redux).
 *
 * price is the GROSS (IVA-included) period total for each item — the exact
 * amount Mercado Pago charges for that line, taken as-is from the cart price.
 * The backend derives sub_total and iva internally (sub_total = total / 1.16);
 * this mapper does not strip IVA or reconcile cents.
 * months is validity only and does NOT multiply or divide the price.
 */

import { ProductByCategory, PipedriveSeller } from 'src/redux/shopping_cart/shopping_cart.initial';
import {
    CartItemPayload,
    PayerPayload,
    InstallmentsPayload,
    CreatePaymentLinkPayload,
} from 'src/apis/mercadoPago';
import {
    SUBCATEGORY_PAYLOAD_OVERRIDE,
    getProductDisplayName,
} from 'src/components/Retention/CommercialCart/ProducsSelectable/CommercialCart.constants';

// ---------------------------------------------------------------------------
// Primitive rounding utility
// ---------------------------------------------------------------------------

/** Round a currency amount to 2 decimal places, immune to binary float error. */
export function round2(amount: number): number {
    return Math.round((amount + Number.EPSILON) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface MapperSelection {
    quantityByProductId: Record<number, number>;
    priceByProductId: Record<number, number>;
}

export interface LoggedUser {
    id?: number;
    email?: string;
}

// ---------------------------------------------------------------------------
// Per-category item building
// ---------------------------------------------------------------------------

/**
 * Build the items array for the payload, applying per-category formulas and
 * the classification_id guard (Destacado only).
 */
export function buildMercadoPagoItems(
    selectedProducts: ProductByCategory[],
    selection: MapperSelection,
    selectedMonths: number,
    commercialUserId?: number,
): CartItemPayload[] {
    return selectedProducts.map((product) => {
        const P = selection.priceByProductId[product.product_id] ?? 0;
        const Q = selection.quantityByProductId[product.product_id] ?? 1;
        const M = selectedMonths;
        const cat = product.category;
        const sub = product.subcategory?.toLowerCase();

        // Defaults cover elite: cart_product_id=product_id, quantity=1, months=M.
        // smart_leads validity is fixed at 12 months; all others use M (validity only).
        let cart_product_id = product.product_id;
        let quantity = 1;
        const months = cat === 'smart_leads' ? 12 : M;
        let classification_id: number | undefined;

        if (cat === 'Destacado') {
            quantity = Q;
            classification_id = 1; // required for Destacado activation
        } else if (cat === 'smart_leads') {
            quantity = Q;
        } else if (cat === 'premium') {
            const override = SUBCATEGORY_PAYLOAD_OVERRIDE[sub as keyof typeof SUBCATEGORY_PAYLOAD_OVERRIDE];
            cart_product_id = override?.cart_product_id ?? 11; // lockdown
        }

        const title = getProductDisplayName(product.name_product, product.subcategory);
        const monthLabel = months === 1 ? 'mes' : 'meses';
        const description = `${quantity} ${title} por ${months} ${monthLabel}`;

        const base: CartItemPayload = {
            cart_product_id,
            price: round2(P),
            quantity,
            months,
            title,
            description,
        };

        if (commercialUserId !== undefined) {
            base.user_id = commercialUserId;
        }

        // classification_id guard — ONLY on Destacado, never on other categories
        if (classification_id !== undefined) {
            base.classification_id = classification_id;
        }

        return base;
    });
}

// ---------------------------------------------------------------------------
// Payer
// ---------------------------------------------------------------------------

/** Build the payer object. phone/rfc are intentionally omitted (not in canSubmit-required state). */
export function buildPayer(
    userProfile: { name: string; lastname: string },
    email: string,
): PayerPayload {
    return {
        name: userProfile.name,
        surname: userProfile.lastname,
        email,
    };
}

// ---------------------------------------------------------------------------
// Installments
// ---------------------------------------------------------------------------

/**
 * Build the installments block when MSI is enabled.
 * Returns an empty object when MSI is off so the spread adds nothing.
 */
export function buildInstallments(
    msiEnabled: boolean,
    msiMonths: number | null,
): InstallmentsPayload | Record<string, never> {
    if (!msiEnabled || msiMonths == null) return {};
    return {
        enable_msi: true,
        max_installments: msiMonths,
        default_installments: msiMonths,
    };
}

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

/**
 * Resolve the commercial agent id used for attribution: the PCOM users.id of
 * the logged agent.
 *
 * Single source for both items[].user_id (persisted in DB) and
 * metadata.seller_id (traceability on the Mercado Pago side), so the two
 * always carry the same value. pipedrive_seller.seller_id is deliberately NOT
 * a fallback: it belongs to pipedrive_sellers, a different identity space.
 */
export function resolveCommercialUserId(
    loggedUser: LoggedUser | null | undefined,
): number | undefined {
    return loggedUser?.id;
}

/**
 * Build attribution fields.
 *
 * Priority chain (nullish = null/undefined; empty string treated as absent):
 *   seller_email = pipedrive_seller?.email ?? loggedUser?.email
 *   metadata.deal_id = pipedrive_seller?.deal_id  (omit when nullish)
 *   metadata.seller_id = loggedUser?.id  (omit when nullish)
 *
 * Empty metadata ({}) is omitted entirely.
 */
export function buildAttribution(
    pipedriveSeller: PipedriveSeller | null | undefined,
    loggedUser: LoggedUser | null | undefined,
): {
    seller_email?: string;
    metadata?: { deal_id?: number | string; seller_id?: number | string };
} {
    const out: {
        seller_email?: string;
        metadata?: { deal_id?: number | string; seller_id?: number | string };
    } = {};

    const email = pipedriveSeller?.email || loggedUser?.email;
    if (email) out.seller_email = email;

    const meta: { deal_id?: number | string; seller_id?: number | string } = {};
    const dealId = pipedriveSeller?.deal_id;
    const sellerId = resolveCommercialUserId(loggedUser);

    if (dealId != null) meta.deal_id = dealId;
    if (sellerId != null) meta.seller_id = sellerId;

    if (Object.keys(meta).length > 0) out.metadata = meta;

    return out;
}

// ---------------------------------------------------------------------------
// Top-level payload builder
// ---------------------------------------------------------------------------

export interface BuildMercadoPagoPayloadParams {
    selectedProducts: ProductByCategory[];
    selection: MapperSelection & { msiEnabled: boolean; msiMonths: number | null };
    selectedMonths: number;
    userId: number;
    userProfile: { name: string; lastname: string };
    payerEmail: string;
    pipedriveSeller: PipedriveSeller | null | undefined;
    loggedUser: LoggedUser | null | undefined;
}

/**
 * Build the complete CreatePaymentLinkPayload.
 *
 * Prohibited fields are NEVER included: total, sub_total, detail, source,
 * origin, payment_method, enable_cash, enable_bank_transfer, ttl_hours.
 * Server computes those automatically.
 */
export function buildMercadoPagoPayload(
    params: BuildMercadoPagoPayloadParams,
): CreatePaymentLinkPayload {
    const commercialUserId = resolveCommercialUserId(params.loggedUser);
    const items = buildMercadoPagoItems(
        params.selectedProducts,
        params.selection,
        params.selectedMonths,
        commercialUserId,
    );
    const payer = buildPayer(params.userProfile, params.payerEmail);
    const installments = buildInstallments(params.selection.msiEnabled, params.selection.msiMonths);
    const attribution = buildAttribution(params.pipedriveSeller, params.loggedUser);

    return {
        country: 'MX',
        user_id: params.userId,
        items,
        payer,
        ...installments,
        ...attribution,
    };
}
